import { useApi, useBearer } from '@healthblocks-io/core'
import Axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from 'usehooks-ts'

import type { MedicationSearchResult } from '@/modules/app/types/HealthParameter.types'

const MINIMUM_CHARACTERS_IN_MEDICATION_SEARCH = 3

export function useMedicationSearch(query: string): {
  results: MedicationSearchResult[]
  isLoading: boolean
} {
  const [results, setResults] = useState<MedicationSearchResult[]>([])
  const [isFetching, setIsFetching] = useState(false)

  const debouncedQuery = useDebounce(query, 300)
  const baseURL = useApi()
  const bearer = useBearer()
  const axios = useMemo(() => Axios.create({ baseURL }), [baseURL])

  // Track the latest request so stale responses from slow/cancelled fetches are ignored
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (debouncedQuery.trim().length < MINIMUM_CHARACTERS_IN_MEDICATION_SEARCH) {
      requestIdRef.current++
      setResults([])
      setIsFetching(false)
      return
    }

    const requestId = ++requestIdRef.current
    setIsFetching(true)

    const doSearch = async () => {
      const bearerHeaders = await bearer()
      const response = await axios.get<MedicationSearchResult[]>('/views/patient/medication/search', {
        params: { q: debouncedQuery.trim() },
        headers: { ...bearerHeaders },
      })
      if (requestId === requestIdRef.current) {
        setResults(response.data)
        setIsFetching(false)
      }
    }

    doSearch().catch(() => {
      if (requestId === requestIdRef.current) {
        setResults([])
        setIsFetching(false)
      }
    })
  }, [debouncedQuery, axios, bearer])

  const isLoading = isFetching || (query.trim().length >= 2 && query !== debouncedQuery)

  return { results, isLoading }
}
